import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import api from '../lib/api';

export interface TransmissionCongestionProperties {
  county: string;
  constraint_count: number;
  max_hours_binding: number;
  avg_hours_binding: number;
  max_avg_shadow_price: number;
  total_binding_hours: number;
  severity: string;
}

interface Constraint {
  constraint_name: string;
  contingency_name: string | null;
  from_station: string | null;
  to_station: string | null;
  from_station_kv: number | null;
  to_station_kv: number | null;
  hours_binding: number;
  avg_shadow_price: string;
  max_shadow_price: string;
  from_county: string | null;
  to_county: string | null;
}

interface DetailData {
  summary: {
    total_constraints: number;
    severe: number;
    moderate: number;
    period_start: string;
    period_end: string;
  };
  constraints: Constraint[];
}

interface GenerationData {
  county: string;
  summary: {
    zone: string;
    total_units: number;
    installed_mw: number;
    available_mw: number;
    gas_units: number;
    gas_mw: number;
    mothballed_units: number;
    mothballed_mw: number | null;
    mora_month: string;
  } | null;
  dispatchable_units: Array<{
    unit_name: string;
    fuel: string;
    zone: string;
    in_service_year: number;
    installed_mw: number;
    available_mw: number;
  }>;
}

const SEVERITY_COLORS: Record<string, string> = {
  severe: '#dc2626',
  high: '#f97316',
  moderate: '#eab308',
  low: '#22c55e',
};

const SEVERITY_LABELS: Record<string, string> = {
  severe: 'Severe Congestion',
  high: 'High Congestion',
  moderate: 'Moderate Congestion',
  low: 'Low Congestion',
};

interface Props {
  data: TransmissionCongestionProperties;
  onClose: () => void;
}

export const TransmissionCongestionDetail: React.FC<Props> = ({ data: d, onClose }) => {
  const { theme } = useTheme();
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [generation, setGeneration] = useState<GenerationData | null>(null);
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const color = SEVERITY_COLORS[d.severity] || '#94a3b8';

  useEffect(() => {
    api.get('/power/congestion', { params: { county: d.county, min_hours: 10 } })
      .then(res => setDetail(res.data))
      .catch(() => {});
    api.get('/power/generation', { params: { county: d.county } })
      .then(res => setGeneration(res.data))
      .catch(() => {});
  }, [d.county]);

  const gen = generation?.summary;
  const period = detail?.summary;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            {d.county} County
          </h2>
          <div style={{
            marginTop: '4px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: color + '20', color,
            borderRadius: '4px',
          }}>
            {SEVERITY_LABELS[d.severity] || d.severity}
          </div>
          {gen?.zone && (
            <span style={{
              marginLeft: '6px', padding: '2px 6px', fontSize: '9px', fontWeight: 600,
              background: theme.backgroundAlt, color: theme.textSecondary,
              borderRadius: '3px',
            }}>
              ERCOT {gen.zone}
            </span>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Congestion metrics */}
        <Section title="Transmission Congestion">
          <Field label="Binding Constraints" value={String(d.constraint_count)} />
          <Field label="Max Hours Binding" value={`${d.max_hours_binding.toLocaleString()} / ${period ? ((new Date(period.period_end).getTime() - new Date(period.period_start).getTime()) / 3.6e6).toFixed(0) : '720'}h`} />
          <Field label="Total Binding Hours" value={d.total_binding_hours.toLocaleString()} />
          <Field label="Peak Shadow Price" value={`$${d.max_avg_shadow_price.toFixed(1)}/MW`} />
          {period && (
            <Field label="Period" value={`${new Date(period.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(period.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`} />
          )}
        </Section>

        {/* PPA signal */}
        {d.severity === 'severe' || d.severity === 'high' ? (
          <div style={{
            padding: '8px 10px', borderRadius: '6px', marginBottom: '16px',
            background: color + '10', border: `1px solid ${color}30`,
            fontSize: '10px', color, lineHeight: 1.5,
          }}>
            Transmission lines serving this county are frequently at capacity. New load interconnection (PPA delivery) will face congestion charges and potential curtailment risk.
          </div>
        ) : d.severity === 'low' ? (
          <div style={{
            padding: '8px 10px', borderRadius: '6px', marginBottom: '16px',
            background: '#22c55e10', border: '1px solid #22c55e30',
            fontSize: '10px', color: '#22c55e', lineHeight: 1.5,
          }}>
            Transmission congestion is low. Grid has capacity headroom for new load — favorable for PPA delivery without significant congestion charges.
          </div>
        ) : null}

        {/* Generation capacity */}
        {gen && (
          <Section title="Generation Capacity (MORA)">
            <Field label="Total Installed" value={`${gen.installed_mw.toLocaleString()} MW (${gen.total_units} units)`} />
            <Field label="Expected Output" value={`${gen.available_mw.toLocaleString()} MW`} />
            {gen.gas_mw > 0 && (
              <Field label="Gas-Fired" value={`${gen.gas_mw.toLocaleString()} MW (${gen.gas_units} units)`} />
            )}
            {gen.mothballed_units > 0 && (
              <Field label="Mothballed" value={`${gen.mothballed_mw?.toLocaleString() || '?'} MW (${gen.mothballed_units} units)`} />
            )}
            <Field label="Data Month" value={gen.mora_month} />
          </Section>
        )}

        {/* Dispatchable units (PPA counterparties) */}
        {generation && generation.dispatchable_units.length > 0 && (
          <Section title={`PPA Counterparties (${generation.dispatchable_units.length})`}>
            {generation.dispatchable_units.slice(0, 10).map((u, i) => (
              <div key={i} style={{
                padding: '5px 0',
                borderBottom: `1px solid ${theme.borderLight}`,
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: theme.text }}>
                  {u.unit_name}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '2px', fontSize: '10px' }}>
                  <span style={{ color: '#d97706', fontWeight: 600 }}>
                    {u.fuel} {u.installed_mw.toLocaleString()} MW
                  </span>
                  <span style={{ color: theme.textTertiary }}>
                    Output: {u.available_mw.toLocaleString()} MW
                  </span>
                  <span style={{ color: theme.textTertiary }}>
                    Since {u.in_service_year}
                  </span>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Binding constraints detail */}
        {detail && detail.constraints.length > 0 && (
          <Section title={`Binding Constraints (${detail.constraints.length})`}>
            {detail.constraints.slice(0, 15).map((c, i) => (
              <div key={i} style={{
                padding: '5px 0',
                borderBottom: `1px solid ${theme.borderLight}`,
              }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: theme.text, fontFamily: designTokens.typography.fontFamily.mono }}>
                  {c.from_station || '?'} → {c.to_station || '?'}
                  {c.from_station_kv ? ` (${c.from_station_kv}kV)` : ''}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '2px', fontSize: '10px' }}>
                  <span style={{
                    color: parseInt(String(c.hours_binding)) >= 200 ? '#dc2626' : parseInt(String(c.hours_binding)) >= 100 ? '#f97316' : theme.textSecondary,
                    fontWeight: 600,
                  }}>
                    {c.hours_binding}h binding
                  </span>
                  <span style={{ color: theme.textTertiary }}>
                    avg ${parseFloat(c.avg_shadow_price).toFixed(1)}/MW
                  </span>
                  <span style={{ color: theme.textTertiary }}>
                    peak ${parseFloat(c.max_shadow_price).toFixed(0)}/MW
                  </span>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Data Source */}
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Info size={10} />
            Data Sources
            {sourceExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {sourceExpanded && (
            <div style={{
              marginTop: '6px', padding: '8px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.6,
            }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: '4px' }}>
                ERCOT DAM Shadow Prices (NP6-86-CD)
              </div>
              <div style={{ color: theme.textTertiary }}>
                Day-Ahead Market binding transmission constraints. High hours binding and shadow prices indicate transmission corridors at capacity — new load interconnection faces congestion risk.
              </div>
              <div style={{ color: theme.text, fontWeight: 600, marginTop: '8px', marginBottom: '4px' }}>
                ERCOT MORA Resource Details
              </div>
              <div style={{ color: theme.textTertiary }}>
                Monthly Outlook for Resource Adequacy. Shows generation units by county with installed and available capacity. Gas-fired dispatchable units are potential PPA counterparties.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
